import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LogOut, Search, X } from 'lucide-react';
import { useAuth } from '../../lib/auth';
import { atendimentoApi } from '../atendimento/lib/api';
import type { Contato, Conversa } from '../atendimento/lib/tipos';
import { ChatAtender } from './ChatAtender';
import { MenuLoja } from './MenuLoja';

// A cara da LOJA dentro do proprio `/app` — o mesmo aplicativo que o cliente
// instala, mostrando a lista de todo mundo quando quem entrou e' da equipe.
//
// ⚠️ **NAO e' um endereco separado, e essa foi a correcao mais importante desta
// onda.** A primeira versao nasceu em `/atender`, com manifesto e icone proprios, e
// o Ruan cortou na hora: *"Nao e' um link diferente. E' para abrir o nosso app, esse
// aqui"*. Ele ja tinha o app instalado na tela de inicio; um segundo endereco
// obrigaria a instalar um segundo icone, decorar qual e' qual, e manter duas telas
// que fazem a mesma coisa. **Um app, uma porta — quem muda e' o que ela mostra,
// conforme quem entrou.**
//
// ⚠️ **Fora do `<Layout>` de proposito.** Barra de menus, faixas de alerta e
// dropdowns do ERP nao cabem em 390px e nao sao o assunto aqui. O que ele herda do
// sistema e' a CREDENCIAL (mesmo token, mesmo `useAuth`) — nao a moldura.
//
// ⚠️ **Le por consulta repetida, nao por Socket.IO**, pelo mesmo motivo do app do
// cliente e por um a mais: no celular o navegador suspende o JS quando a tela apaga
// e derruba o WebSocket, e a tela nao recarrega ao reconectar (pendencia aberta —
// "o F5 do WhatsApp"). Perguntar de novo a cada poucos segundos nao tem esse modo
// de falhar, e o custo e' irrelevante numa lista de loja.
//
// **Fase 1: so o RUAN.** Nao ha superficie de API nova aqui — o login dele ja fura o
// device-trust por desenho ([DA-281]), entao o token vale do celular sem
// certificado. Abrir pra vendedora e' decisao de SEGURANCA, nao de codigo: hoje a
// senha delas nao vale de aparelho nenhum sem cert, e passaria a valer.

const COBRE = '#b45309';
const INTERVALO_ATIVO = 6000;
const INTERVALO_OCIOSO = 30000;

// ⚠️ **So a caixa do APLICATIVO.** A primeira versao trazia tambem o WhatsApp, e ele
// cortou no 1o uso: *"Vamos exibir so os usuarios cadastrados"*. O app existe pra
// atender quem se cadastrou NELE — o WhatsApp ja tem o aparelho e a tela do ERP, e
// misturar as duas caixas enterrava 6 cadastrados debaixo de 100 conversas antigas.
// A `compras` nunca esteve aqui: roda no celular pessoal dele e atende fornecedor,
// nao cliente ([DA-513]).
//
// ⚠️ Com uma caixa so, a etiqueta de canal em cada linha e os filtros por canal
// SAIRAM junto — campo que so pode dar um valor nao informa nada, e filtro que nunca
// muda o resultado e' pior que filtro nenhum, porque parece que faz.
type Filtro = 'tudo' | 'sem_resposta';

/** Linha da lista: uma conversa, ou um cadastrado do app que ainda nao tem conversa. */
interface Linha {
  chave: string;
  conversa: Conversa | null;
  /** So quando `conversa` e' null — o contato do app sem conversa aberta. */
  contato: Contato;
  quando: string | null;
  previa: string;
  naoLidas: number;
  dono: string | null;
  /** O cliente falou por ultimo e ninguem respondeu depois. */
  esperando: boolean;
}

function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  if (!partes.length) return '?';
  const a = partes[0]![0] ?? '';
  const b = partes.length > 1 ? (partes[partes.length - 1]![0] ?? '') : '';
  return (a + b).toUpperCase();
}

/** "19:22" hoje, "Ontem", "12/08" antes disso — como o WhatsApp faz. */
function quandoCurto(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const hoje = new Date();
  const ontem = new Date(hoje);
  ontem.setDate(hoje.getDate() - 1);
  const mesmoDia = (x: Date, y: Date) => x.toDateString() === y.toDateString();
  if (mesmoDia(d, hoje)) return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  if (mesmoDia(d, ontem)) return 'Ontem';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

function primeiroNome(nome: string | null): string | null {
  if (!nome) return null;
  return nome.trim().split(/\s+/)[0] ?? null;
}

export function VisaoLoja() {
  const { usuario, logout } = useAuth();

  const [conversas, setConversas] = useState<Conversa[]>([]);
  const [contatosApp, setContatosApp] = useState<Contato[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  /** 403 do device-trust: este APARELHO nao pode atender. Nao adianta tentar de novo. */
  const [naoAutorizado, setNaoAutorizado] = useState(false);
  const [filtro, setFiltro] = useState<Filtro>('tudo');
  const [busca, setBusca] = useState('');
  const [buscaAberta, setBuscaAberta] = useState(false);
  const [abertaId, setAbertaId] = useState<number | null>(null);
  /** Contato do app cuja conversa esta sendo criada agora (evita duplo toque). */
  const [abrindo, setAbrindo] = useState<number | null>(null);

  // A conversa aberta e' o "detalhe" do master/detail. Guardada por ID e nao pelo
  // objeto: a lista se recarrega sozinha a cada ciclo e trocaria a identidade do
  // objeto embaixo do chat.
  const conversaAberta = useMemo(
    () => conversas.find((c) => c.id === abertaId) ?? null,
    [conversas, abertaId],
  );

  const carregar = useCallback(async (primeira: boolean) => {
    try {
      // As duas listas juntas: quem ja conversou e quem so se cadastrou. O pedido
      // dele foi literal — "deve aparecer na lista, todos os contatos cadastrados
      // no novo app" — e a segunda metade nao sai da tabela de conversas, porque a
      // conversa do app so nasce quando o CLIENTE abre o chat.
      const [convs, cts] = await Promise.all([
        atendimentoApi.listarConversas({ inbox: 'app', limit: 200 }),
        atendimentoApi.listarContatos({ inbox: 'app', limit: 200 }),
      ]);
      setConversas(convs);
      setContatosApp(cts);
      setErro(null);
      setNaoAutorizado(false);
    } catch (e) {
      // ⚠️ **403 nao e' falha de rede, e tratar os dois igual esconde a unica coisa
      // que o operador precisa saber.** Aparelho sem certificado toma 403 aqui pra
      // sempre (device-trust), entao "vou tentar de novo sozinho" seria mentira: nao
      // adianta esperar, e ninguem ia descobrir o motivo olhando a tela. Foi o que
      // aconteceu no celular do balcao — o atalho abria a visao da loja, a lista nunca
      // vinha, e o aviso dizia que estava tentando.
      if ((e as { status?: number }).status === 403) {
        setNaoAutorizado(true);
        setErro(null);
        return;
      }
      // Falha de rede nao apaga o que ja esta na tela — num celular ela e' rotina,
      // e esvaziar a lista faria parecer que a loja parou de receber.
      if (primeira) setErro('Nao consegui carregar agora. Vou tentar de novo sozinho.');
    } finally {
      if (primeira) setCarregando(false);
    }
  }, []);

  // Consulta repetida, com ritmo menor quando a tela esta escondida — e uma volta
  // imediata quando o celular sai do bolso, senao a lista fica ate 30s velha
  // exatamente no segundo em que ele olha pra ela.
  const carregarRef = useRef(carregar);
  carregarRef.current = carregar;
  useEffect(() => {
    let vivo = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    async function ciclo(primeira: boolean) {
      await carregarRef.current(primeira);
      if (!vivo) return;
      timer = setTimeout(() => void ciclo(false), document.hidden ? INTERVALO_OCIOSO : INTERVALO_ATIVO);
    }
    function aoVoltar() {
      if (document.hidden || !vivo) return;
      if (timer) clearTimeout(timer);
      void ciclo(false);
    }
    document.addEventListener('visibilitychange', aoVoltar);
    void ciclo(true);
    return () => {
      vivo = false;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', aoVoltar);
    };
  }, []);

  const linhas = useMemo<Linha[]>(() => {
    const comConversa = new Set(conversas.map((c) => c.contato_id));
    const deConversas: Linha[] = conversas
      .map((c) => ({
        chave: `c${c.id}`,
        conversa: c,
        contato: c.contato,
        quando: c.ultima_mensagem_em,
        previa: c.ultima_mensagem_preview ?? '',
        naoLidas: c.nao_lidas ?? 0,
        dono: primeiroNome(c.usuario_atribuido_login || c.usuario_atribuido_nome),
        esperando: c.ultima_mensagem_direcao === 'inbound',
      }));

    // Cadastrado no app que nunca abriu o chat: aparece com o mesmo peso, sem hora
    // e com o motivo escrito. ⚠️ Nao vai pro topo por causa disso — `quando` nulo
    // ordenado em DESC subiria a lista inteira ([DA-367]); a ordenacao abaixo joga
    // os sem-hora pro fim de proposito.
    const soCadastrados: Linha[] = contatosApp
      .filter((ct) => !comConversa.has(ct.id))
      .map((ct) => ({
        chave: `t${ct.id}`,
        conversa: null,
        contato: ct,
        quando: null,
        previa: 'Cadastrou no aplicativo e ainda nao escreveu',
        naoLidas: 0,
        dono: null,
        esperando: false,
      }));

    // ⚠️ Os dois grupos ficam SEPARADOS, e isso foi medido, nao suposto: misturados
    // e ordenados por recencia, o cadastrado sem conversa caiu na **posicao 103 de
    // 107** — presente na tela e invisivel na pratica, que e' a [DA-468] com outra
    // roupa. Jogar ele pro topo da mesma lista seria pior: a ordenacao passaria a
    // mentir sobre recencia. Entao ele vai num bloco proprio, com o titulo dizendo o
    // que aquilo e' — a informacao muda de lugar, nao evapora ([DA-403]).
    const ordenar = (a: Linha, b: Linha) => {
      if (!a.quando && !b.quando) return a.contato.nome.localeCompare(b.contato.nome);
      if (!a.quando) return 1;
      if (!b.quando) return -1;
      return new Date(b.quando).getTime() - new Date(a.quando).getTime();
    };
    const todas = [...soCadastrados.sort(ordenar), ...deConversas.sort(ordenar)];

    const porFiltro = filtro === 'sem_resposta' ? todas.filter((l) => l.esperando) : todas;

    const termo = busca.trim().toLowerCase();
    if (!termo) return porFiltro;
    return porFiltro.filter((l) => {
      const alvo = `${l.contato.nome} ${l.contato.push_name ?? ''} ${l.contato.telefone ?? ''}`;
      return alvo.toLowerCase().includes(termo);
    });
  }, [conversas, contatosApp, filtro, busca]);

  async function abrir(linha: Linha) {
    if (linha.conversa) {
      setAbertaId(linha.conversa.id);
      if (linha.naoLidas > 0) {
        atendimentoApi.marcarLida(linha.conversa.id).catch(() => { /* a lista corrige no proximo ciclo */ });
      }
      return;
    }
    // Cadastrado sem conversa: ela nasce agora, no primeiro alo da loja.
    if (abrindo) return;
    setAbrindo(linha.contato.id);
    try {
      const nova = await atendimentoApi.novaConversa({ jid: linha.contato.jid, inbox: 'app' });
      setConversas((atuais) => (atuais.some((c) => c.id === nova.id) ? atuais : [nova, ...atuais]));
      setAbertaId(nova.id);
    } catch {
      setErro('Nao consegui abrir a conversa com este cadastro.');
    } finally {
      setAbrindo(null);
    }
  }

  if (conversaAberta) {
    return (
      <ChatAtender
        conversa={conversaAberta}
        aoVoltar={() => { setAbertaId(null); void carregar(false); }}
      />
    );
  }

  const chips: Array<{ id: Filtro; texto: string }> = [
    { id: 'tudo', texto: 'Tudo' },
    { id: 'sem_resposta', texto: 'Sem resposta' },
  ];

  return (
    <div
      className="app-atender-root"
      style={{ height: '100dvh', display: 'flex', flexDirection: 'column', backgroundColor: '#f7f7f7', color: '#111' }}
    >
      <header style={{ backgroundColor: COBRE, color: '#fff', padding: '11px 8px 11px 4px', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        {/* O ☰ fica na ESQUERDA, no mesmo lugar do menu do app do cliente — o dedo
            ja aprendeu esse canto, e o mesmo aplicativo nao pode mudar de gramatica
            conforme quem entrou. */}
        <MenuLoja nome={usuario?.nome ?? ''} perfil={usuario?.perfil ?? ''} aoSair={() => void logout()} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16.5, fontWeight: 800, letterSpacing: 0.3 }}>FIO DE COBRE</div>
          <div style={{ fontSize: 12, opacity: 0.9, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            Atendimento · {usuario?.nome ?? ''}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setBuscaAberta((v) => !v)}
          aria-label={buscaAberta ? 'Fechar a busca' : 'Procurar cliente'}
          style={{ width: 42, height: 42, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}
        >
          {buscaAberta ? <X size={20} /> : <Search size={20} />}
        </button>
        <button
          type="button"
          onClick={() => void logout()}
          aria-label="Sair"
          style={{ width: 42, height: 42, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}
        >
          <LogOut size={19} />
        </button>
      </header>

      {buscaAberta && (
        <div style={{ backgroundColor: '#fff', padding: '9px 12px', borderBottom: '1px solid #eceaea', flexShrink: 0 }}>
          <input
            className="no-upper"
            autoFocus
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Procurar pelo nome ou telefone..."
            style={{ width: '100%', fontSize: 16, padding: '10px 14px', borderRadius: 20, border: '1px solid #d1d5db', backgroundColor: '#fff', color: '#111' }}
          />
        </div>
      )}

      <div style={{ display: 'flex', gap: 7, padding: '9px 12px', backgroundColor: '#fff', borderBottom: '1px solid #eceaea', overflowX: 'auto', flexShrink: 0 }}>
        {chips.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => setFiltro(c.id)}
            style={{
              flexShrink: 0,
              fontSize: 12.5,
              fontWeight: 700,
              padding: '6px 13px',
              borderRadius: 16,
              whiteSpace: 'nowrap',
              backgroundColor: filtro === c.id ? COBRE : '#f1f2f4',
              color: filtro === c.id ? '#fff' : '#475569',
            }}
          >
            {c.texto}
          </button>
        ))}
      </div>

      {erro && (
        <div style={{ backgroundColor: '#fdf0e6', color: '#7c2d12', padding: '8px 14px', fontSize: 13, flexShrink: 0 }}>{erro}</div>
      )}

      <div style={{ flex: 1, overflowY: 'auto', backgroundColor: '#fff' }}>
        {naoAutorizado ? (
          /* ⚠️ Diz a CAUSA e o conserto, e nao promete tentar de novo. Este aparelho
             vai tomar 403 pra sempre ate ganhar certificado — foi o caso do celular do
             balcao, onde a lista nunca vinha e o aviso dizia que estava carregando. */
          <div style={{ textAlign: 'center', color: '#7c2d12', fontSize: 14, marginTop: 30, padding: '0 26px', lineHeight: 1.6 }}>
            <div style={{ fontWeight: 700, fontSize: 15.5 }}>Este aparelho ainda nao pode atender</div>
            <p style={{ marginTop: 8, color: '#57534e' }}>
              Ele nao tem o certificado da loja instalado, entao o sistema recusa a lista de
              conversas. Quem instala e o Ruan, em Configuracoes → Dispositivos Autorizados.
            </p>
          </div>
        ) : carregando ? (
          <p style={{ textAlign: 'center', color: '#6b7280', fontSize: 14, marginTop: 26 }}>Carregando...</p>
        ) : linhas.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#6b7280', fontSize: 14, marginTop: 30, padding: '0 26px', lineHeight: 1.6 }}>
            {/* ⚠️ Nunca "nao existe": a busca so enxerga o que esta carregado, e dizer
                "nao encontrado" faria o operador concluir que o cliente nao esta
                cadastrado ([DA-468]/[DA-501]). */}
            {busca.trim()
              ? <>Nenhuma das <b>{conversas.length + contatosApp.length}</b> conversas carregadas casa com “{busca.trim()}”.</>
              : 'Nada por aqui ainda.'}
          </div>
        ) : (
          linhas.map((l, i) => (
            <div key={l.chave}>
              {/* Titulo de cada bloco, so quando ele existe — faixa que fica dizendo
                  "esta tudo certo" e' ruido. O primeiro so aparece quando alguem se
                  cadastrou e nao escreveu; hoje isso e' zero. */}
              {i === 0 && !l.conversa && (
                <div style={{ padding: '9px 13px 5px', fontSize: 11, fontWeight: 700, color: '#9a3412', backgroundColor: '#fdf7f2', letterSpacing: 0.5 }}>
                  CADASTROU NO APP E AINDA NAO ESCREVEU
                </div>
              )}
              {!!l.conversa && !!linhas[i - 1] && !linhas[i - 1]!.conversa && (
                <div style={{ padding: '9px 13px 5px', fontSize: 11, fontWeight: 700, color: '#64748b', backgroundColor: '#f8f8f9', letterSpacing: 0.5 }}>
                  CONVERSAS
                </div>
              )}
            <button
              type="button"
              onClick={() => void abrir(l)}
              disabled={abrindo === l.contato.id}
              style={{
                width: '100%',
                display: 'flex',
                gap: 11,
                alignItems: 'center',
                padding: '11px 13px',
                borderBottom: '1px solid #f2f1f0',
                textAlign: 'left',
                backgroundColor: '#fff',
              }}
            >
              {l.contato.foto_url ? (
                <img
                  src={l.contato.foto_url}
                  alt=""
                  style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                />
              ) : (
                <span
                  style={{
                    width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 14.5, fontWeight: 700,
                    backgroundColor: '#fde8d6', color: '#7c2d12',
                  }}
                >
                  {iniciais(l.contato.nome || l.contato.push_name || '?')}
                </span>
              )}

              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 14.5, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {l.contato.nome || l.contato.push_name || l.contato.telefone}
                </span>
                <span
                  style={{
                    display: 'block', fontSize: 12.5, marginTop: 2,
                    color: l.conversa ? '#64748b' : '#9a3412',
                    fontStyle: l.conversa ? 'normal' : 'italic',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}
                >
                  {abrindo === l.contato.id ? 'Abrindo...' : l.previa}
                </span>
                {l.dono && (
                  <span style={{ display: 'block', fontSize: 10.5, color: '#94a3b8', marginTop: 1 }}>
                    {l.dono} atende
                  </span>
                )}
              </span>

              <span style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5 }}>
                <span style={{ fontSize: 11, color: '#94a3b8', whiteSpace: 'nowrap' }}>{quandoCurto(l.quando)}</span>
                {l.naoLidas > 0 && (
                  <span
                    style={{
                      backgroundColor: COBRE, color: '#fff', fontSize: 11, fontWeight: 700,
                      minWidth: 20, height: 20, borderRadius: 10, padding: '0 5px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    {l.naoLidas}
                  </span>
                )}
              </span>
            </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
